import React, { useState, useEffect, useMemo, useRef } from "react";
import { saveB2BSale, validateB2BFacture, tvaRateForProduct, TVA_RATES } from "../../data/b2bSales";

export default function FactureEditorModal({ sale, clients, catalogProducts, taxRates, onClose, onSave }) {
  const [draft, setDraft] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState("");
  const searchRef = useRef(null);

  useEffect(() => { setDraft(sale ? JSON.parse(JSON.stringify(sale)) : null); }, [sale]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isFacture = draft && draft.documentType === "facture";
  const isLocked = isFacture && draft.validated;
  const client = clients.find((c) => c.id === draft?.clientId) || null;

  const filteredProducts = useMemo(() => {
    if (!searchQuery) return [];
    return (catalogProducts || []).filter((p) =>
      (p.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.barcode || "").includes(searchQuery)
    ).slice(0, 10);
  }, [searchQuery, catalogProducts]);

  if (!draft) return null;

  const recompute = (items) => {
    let totalHT = 0;
    let totalTVA = 0;
    items.forEach((it) => {
      totalHT += Number(it.lineTotalHT || 0);
      totalTVA += Number(it.lineTotalHT || 0) * Number(it.tvaRate || 0);
    });
    totalHT = Number(totalHT.toFixed(2));
    totalTVA = Number(totalTVA.toFixed(2));
    return { items, totalHT, totalTVA, totalTTC: Number((totalHT + totalTVA).toFixed(2)) };
  };

  const updateField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const updateLine = (idx, field, value) => {
    const items = [...draft.items];
    items[idx] = { ...items[idx], [field]: value };
    const qty = Number(items[idx].quantity || 1);
    const salePrice = Number(items[idx].salePrice || 0);
    const tva = Number(items[idx].tvaRate || 0.1);
    items[idx].lineTotalHT = Number((qty * salePrice).toFixed(2));
    items[idx].lineTotalTTC = Number((qty * salePrice * (1 + tva)).toFixed(2));
    setDraft(recompute(items));
  };

  const removeLine = (idx) => setDraft(recompute(draft.items.filter((_, i) => i !== idx)));

  const handleAddProduct = (product) => {
    const items = [...draft.items];
    const existingIndex = items.findIndex((i) => i.productId === product.id);
    if (existingIndex >= 0) {
      items[existingIndex] = { ...items[existingIndex], quantity: items[existingIndex].quantity + 1 };
      const qty = items[existingIndex].quantity;
      const price = items[existingIndex].salePrice;
      const tva = items[existingIndex].tvaRate || 0.1;
      items[existingIndex].lineTotalHT = Number((qty * price).toFixed(2));
      items[existingIndex].lineTotalTTC = Number((qty * price * (1 + tva)).toFixed(2));
    } else {
      const tva = tvaRateForProduct(product, taxRates);
      const salePrice = Number(product.price || 0);
      items.push({
        productId: product.id,
        productName: product.name,
        quantity: 1,
        unitCost: product.purchasePrice || product.costPrice || 0,
        salePrice,
        tvaRate: tva,
        lineTotalHT: Number(salePrice.toFixed(2)),
        lineTotalTTC: Number((salePrice * (1 + tva)).toFixed(2)),
      });
    }
    setDraft(recompute(items));
    setSearchQuery("");
  };

  const handleSelectClient = (clientId) => {
    const selected = clients.find((c) => c.id === clientId);
    const first = selected && selected.deliveryAddresses && selected.deliveryAddresses[0];
    setDraft((prev) => ({
      ...prev,
      clientId,
      deliveryAddressId: first ? first.id : "",
      deliveryAddressText: first ? first.address : "",
    }));
  };

  const persist = async (doValidate) => {
    if (!draft.clientId) { setToast("Veuillez sélectionner un client."); return null; }
    if (!draft.items || draft.items.length === 0) { setToast("Veuillez ajouter au moins un produit."); return null; }
    if (isFacture && !draft.deliveryAddressText && clients.find((c) => c.id === draft.clientId)?.deliveryAddresses?.length) {
      setToast("Veuillez choisir une adresse de livraison."); return null;
    }
    try {
      const saved = doValidate ? await validateB2BFacture(draft) : await saveB2BSale(draft);
      onSave(saved);
      onClose();
      return saved;
    } catch (err) {
      setToast(err.message || "Erreur lors de l'enregistrement.");
      return null;
    }
  };

  const tvaRateLabel = (tvaRate) => `${(Number(tvaRate) * 100).toFixed(0)} %`;
  const fmt = (v) => `${Number(v || 0).toFixed(2)} €`;
  const margin = (cost, price) => (price > 0 ? (((price - cost) / price) * 100).toFixed(1) : "0.0");

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.75)", zIndex: 1070 }}>
      <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: "1100px" }}>
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header">
            <h5 className="modal-title">
              {isLocked ? "Facture validée" : isFacture ? "Facture" : "Devis"}
              {draft.documentNumber ? <span className="text-muted ms-2">— {draft.documentNumber}</span> : null}
              {isFacture && !draft.validated ? <span className="badge bg-warning text-dark ms-2">BROUILLON</span> : null}
              {isLocked ? <span className="badge bg-success ms-2">VALIDÉE</span> : null}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            {toast && <div className="alert alert-warning py-2 px-3">{toast}</div>}

            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label text-muted small mb-1">Client</label>
                <select
                  className="form-select form-select-sm"
                  value={draft.clientId}
                  disabled={isLocked}
                  onChange={(e) => handleSelectClient(e.target.value)}
                >
                  <option value="">Sélectionner un client...</option>
                  {(clients || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.companyName}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label text-muted small mb-1">Adresse de livraison</label>
                {isFacture ? (
                  <select
                    className="form-select form-select-sm"
                    value={draft.deliveryAddressId}
                    disabled={isLocked}
                    onChange={(e) => {
                      const addr = (client?.deliveryAddresses || []).find((a) => a.id === e.target.value);
                      setDraft((prev) => ({ ...prev, deliveryAddressId: e.target.value, deliveryAddressText: addr ? addr.address : "" }));
                    }}
                  >
                    <option value="">Choisir une adresse...</option>
                    {(client?.deliveryAddresses || []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label ? a.label + " — " : ""}{a.address}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="small text-muted pt-1">Les devis utilisent l'adresse principale du client.</div>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label text-muted small mb-1">Échéance / Livraison</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={draft.expectedDate || ""}
                  disabled={isLocked}
                  onChange={(e) => updateField("expectedDate", e.target.value)}
                />
              </div>
            </div>

            <label className="form-label text-muted small mb-1">Notes</label>
            <textarea
              className="form-control form-control-sm mb-3"
              rows="2"
              value={draft.notes || ""}
              disabled={isLocked}
              placeholder="Notes visibles sur le document (conditions, livraison...)"
              onChange={(e) => updateField("notes", e.target.value)}
            />

            <table className="table table-sm table-bordered bg-white">
              <thead className="table-secondary text-muted">
                <tr>
                  <th>Produit</th>
                  <th style={{ width: "8%" }}>Qté</th>
                  <th style={{ width: "13%" }}>P.V. HT</th>
                  <th style={{ width: "10%" }}>TVA</th>
                  <th style={{ width: "12%" }}>Total HT</th>
                  <th style={{ width: "12%" }}>Total TTC</th>
                  <th style={{ width: "8%" }}>Marge</th>
                  <th style={{ width: "4%" }}></th>
                </tr>
              </thead>
              <tbody>
                {(draft.items || []).map((item, idx) => {
                  const m = margin(item.unitCost, item.salePrice);
                  return (
                    <tr key={idx}>
                      <td className="align-middle"><strong>{item.productName}</strong></td>
                      <td>
                        <input type="number" min="1" className="form-control form-control-sm" value={item.quantity} disabled={isLocked}
                          onChange={(e) => updateLine(idx, "quantity", Number(e.target.value) || 0)} />
                      </td>
                      <td>
                        <input type="number" step="0.01" min="0" className="form-control form-control-sm fw-bold text-primary" value={item.salePrice} disabled={isLocked}
                          onChange={(e) => updateLine(idx, "salePrice", Number(e.target.value) || 0)} />
                      </td>
                      <td>
                        <select className="form-select form-select-sm" value={Number(item.tvaRate) || 0.1} disabled={isLocked}
                          onChange={(e) => updateLine(idx, "tvaRate", Number(e.target.value))}>
                          {TVA_RATES.map((r) => (
                            <option key={r} value={r}>{tvaRateLabel(r)}</option>
                          ))}
                        </select>
                      </td>
                      <td className="align-middle fw-bold">{fmt(item.lineTotalHT)}</td>
                      <td className="align-middle fw-bold">{fmt(item.lineTotalTTC)}</td>
                      <td className="align-middle">
                        <span className={`badge ${Number(m) < 15 ? "bg-warning text-dark" : "bg-success"}`}>{m}%</span>
                      </td>
                      <td className="text-center align-middle">
                        {!isLocked && (
                          <button type="button" className="btn btn-sm btn-outline-danger border-0" onClick={() => removeLine(idx)}><i className="fas fa-times"></i></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {!isLocked && (
              <div className="position-relative mb-3 w-50">
                <input
                  ref={searchRef}
                  type="text"
                  className="form-control form-control-sm bg-white"
                  placeholder="+ Ajouter un produit (Recherche ou Scan...)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {filteredProducts.length > 0 && (
                  <ul className="list-group position-absolute w-100 mt-1 shadow" style={{ zIndex: 1000, maxHeight: "200px", overflowY: "auto" }}>
                    {filteredProducts.map((product) => (
                      <button
                        type="button"
                        key={product.id}
                        className="list-group-item list-group-item-action py-1 d-flex justify-content-between"
                        onClick={() => handleAddProduct(product)}
                      >
                        <span>{product.name}</span>
                        <span className="badge bg-primary rounded-pill">{product.price} €</span>
                      </button>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mb-2 fs-6">
              <div className="me-4">
                <span className="text-muted">Total HT : </span>
                <strong>{fmt(draft.totalHT)}</strong>
              </div>
              <div className="me-4">
                <span className="text-muted">TVA : </span>
                <strong>{fmt(draft.totalTVA)}</strong>
              </div>
              <div>
                <span className="text-muted">Total TTC : </span>
                <strong className="fs-5" style={{ color: "#166534" }}>{fmt(draft.totalTTC)}</strong>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>Fermer</button>
            {!isLocked && (
              <>
                <button className="btn btn-outline-primary" onClick={() => persist(false)}><i className="fas fa-save me-2"></i>Enregistrer</button>
                {isFacture && (
                  <button className="btn btn-success" onClick={() => persist(true)}>
                    <i className="fas fa-check-circle me-2"></i>Valider la facture
                  </button>
                )}
              </>
            )}
            {draft.documentNumber && (
              <button className="btn btn-primary" disabled><i className="fas fa-print me-2"></i>Imprimer (via liste)</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
